import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import ChannelDisplayNameField from '../components/ChannelDisplayNameField';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2, Plug, CheckCircle2, XCircle, ArrowLeft, Boxes, Link2, Trash2, FileText, Plus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Flipkart Marketplace integration page — /integrations/flipkart.
 * Supports MULTIPLE Flipkart seller accounts per tenant. Each account has
 * its own credentials, default location, warehouse mapping and syncs.
 */
const FlipkartIntegrationPage = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);   // channel_id or 'new'
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Credentials form
  const [accountName, setAccountName] = useState('');
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [environment, setEnvironment] = useState('production');
  const [defaultLocationId, setDefaultLocationId] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Mapping dialog
  const [mapOpen, setMapOpen] = useState(false);
  const [opswarexWarehouses, setOpswarexWarehouses] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [savingMap, setSavingMap] = useState(false);

  const token = () => localStorage.getItem('token');
  const headers = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const chq = (id) => (id && id !== 'new' ? `?channel_id=${id}` : '');

  const [orderStats, setOrderStats] = useState(null);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [listingsJob, setListingsJob] = useState(null);
  const [startingListings, setStartingListings] = useState(false);
  const listingsPollRef = useRef(null);
  const [returns, setReturns] = useState([]);
  const [syncingReturns, setSyncingReturns] = useState(false);
  const [splitLabelsInvoice, setSplitLabelsInvoice] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const isNew = selectedId === 'new';

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/integrations/flipkart/accounts`, headers());
      const list = r.data?.accounts || [];
      setAccounts(list);
      let sel = selectedId;
      if (!sel || (sel !== 'new' && !list.find(a => a.channel_id === sel))) {
        sel = list.length ? list[0].channel_id : 'new';
        setSelectedId(sel);
      }
      if (sel && sel !== 'new') await loadAccount(sel, list);
      else resetForm();
    } catch (e) {
      toast.error('Failed to load Flipkart accounts');
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setStatus(null); setOrderStats(null); setReturns([]); setListingsJob(null);
    setAccountName(''); setAppId(''); setAppSecret('');
    setEnvironment('production'); setDefaultLocationId(''); setSplitLabelsInvoice(false);
    setMappingRows([]);
  };

  const loadAccount = async (channelId, list) => {
    try {
      const r = await axios.get(`${API_BASE_URL}/integrations/flipkart/status${chq(channelId)}`, headers());
      setStatus(r.data);
      setAccountName(r.data?.name || '');
      setDefaultLocationId(r.data?.default_location_id || '');
      setEnvironment(r.data?.environment || 'production');
      setMappingRows(r.data?.warehouse_mapping || []);
      setSplitLabelsInvoice(!!r.data?.split_labels_invoice);
      setAppId(''); setAppSecret('');
      if (r.data?.connected) {
        try {
          const os = await axios.get(`${API_BASE_URL}/integrations/flipkart/orders/stats${chq(channelId)}`, headers());
          setOrderStats(os.data);
        } catch { /* ignore */ }
        try {
          const rr = await axios.get(`${API_BASE_URL}/integrations/flipkart/fulfillment/returns?limit=50`, headers());
          setReturns(rr.data?.returns || []);
        } catch { /* ignore */ }
        try {
          const js = await axios.get(`${API_BASE_URL}/integrations/flipkart/listings/sync-status${chq(channelId)}`, headers());
          setListingsJob(js.data?.job || null);
          if (js.data?.job?.status === 'running') startListingsPolling(channelId);
        } catch { /* ignore */ }
      } else {
        setOrderStats(null); setReturns([]); setListingsJob(null);
      }
    } catch (e) {
      toast.error('Failed to load Flipkart status');
    }
  };

  const selectAccount = (id) => {
    stopListingsPolling();
    if (id === 'new') resetForm();
    setSelectedId(id);
    if (id !== 'new') loadAccount(id);
  };

  const syncOrdersNow = async () => {
    setSyncingOrders(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/integrations/flipkart/orders/sync-now${chq(selectedId)}`, {}, headers());
      if (r.data?.status === 'ok') {
        toast.success(`Synced: ${r.data.created} created, ${r.data.on_hold} on hold, ${r.data.released} released`);
      } else {
        toast.error(r.data?.error || 'Sync skipped');
      }
      loadAccount(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncingOrders(false); }
  };

  const stopListingsPolling = () => {
    if (listingsPollRef.current) { clearInterval(listingsPollRef.current); listingsPollRef.current = null; }
  };

  const startListingsPolling = (channelId) => {
    stopListingsPolling();
    listingsPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/integrations/flipkart/listings/sync-status${chq(channelId)}`, headers());
        const job = r.data?.job;
        setListingsJob(job);
        if (job && job.status !== 'running') {
          stopListingsPolling();
          if (job.status === 'done') {
            toast.success(`Listings sync complete: ${job.listed} listed, ${job.not_listed} not listed (${job.checked} SKUs)`);
          } else {
            toast.error(`Listings sync failed: ${job.error || 'unknown error'}`);
          }
          loadAccount(channelId);
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  useEffect(() => () => stopListingsPolling(), []);

  const syncListingsNow = async () => {
    setStartingListings(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/integrations/flipkart/listings/sync-now${chq(selectedId)}`, {}, headers());
      setListingsJob(r.data?.job || null);
      if (r.data?.status === 'already_running') toast.info('Listings sync already running');
      else toast.success('Listings sync started — running in background');
      startListingsPolling(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Listings sync failed to start');
    } finally { setStartingListings(false); }
  };

  const syncReturnsNow = async () => {
    setSyncingReturns(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/integrations/flipkart/fulfillment/returns/sync-now`, {}, headers());
      if (r.data?.status === 'ok') {
        toast.success(`Pulled ${r.data.pulled || 0}, ingested ${r.data.ingested || 0}, restocked ${r.data.restocked || 0}`);
      } else if (r.data?.status === 'skipped') {
        toast.info('Flipkart not configured');
      } else {
        toast.error(r.data?.error || 'Returns sync failed');
      }
      loadAccount(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Returns sync failed');
    } finally { setSyncingReturns(false); }
  };

  const toggleSplitLabelsInvoice = async (next) => {
    setSavingSettings(true);
    setSplitLabelsInvoice(next);
    try {
      await axios.patch(`${API_BASE_URL}/integrations/flipkart/settings${chq(selectedId)}`,
        { split_labels_invoice: next }, headers());
      toast.success(next
        ? 'Split enabled — shipping label & tax invoice will download as separate PDFs'
        : 'Combined — shipping label & invoice will download as a single PDF');
    } catch (e) {
      setSplitLabelsInvoice(!next);
      toast.error(e.response?.data?.detail || 'Failed to save setting');
    } finally { setSavingSettings(false); }
  };

  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    if (!appId || !appSecret) { toast.error('App ID and App Secret required'); return; }
    setConnecting(true);
    try {
      const body = {
        app_id: appId, app_secret: appSecret, environment,
        default_location_id: defaultLocationId || null,
        account_name: accountName || null,
      };
      if (!isNew) body.channel_id = selectedId;
      const r = await axios.post(`${API_BASE_URL}/integrations/flipkart/connect`, body, headers());
      toast.success('Connected to Flipkart successfully');
      setAppSecret('');
      const newId = r.data?.channel_id;
      setSelectedId(newId);
      await loadAccounts();
      if (newId) await loadAccount(newId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this Flipkart account? Push sync will stop immediately.')) return;
    try {
      await axios.post(`${API_BASE_URL}/integrations/flipkart/disconnect${chq(selectedId)}`, {}, headers());
      toast.success('Flipkart account disconnected');
      loadAccounts();
    } catch (e) { toast.error('Disconnect failed'); }
  };

  const openMapping = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/integrations/flipkart/opswarex-warehouses`, headers());
      setOpswarexWarehouses(r.data?.warehouses || []);
      setMappingRows(status?.warehouse_mapping?.length
        ? [...status.warehouse_mapping]
        : (r.data?.warehouses || []).map(w => ({
            opswarex_location_id: w.id, channel_location_id: '',
          })));
      setMapOpen(true);
    } catch (e) { toast.error('Failed to load warehouses'); }
  };

  const updateRow = (idx, field, value) => {
    const next = [...mappingRows];
    next[idx] = { ...next[idx], [field]: value };
    setMappingRows(next);
  };

  const addRow = () => setMappingRows([...mappingRows, { opswarex_location_id: '', channel_location_id: '' }]);
  const removeRow = (idx) => setMappingRows(mappingRows.filter((_, i) => i !== idx));

  const saveMapping = async () => {
    setSavingMap(true);
    try {
      const rows = mappingRows.filter(r => r.opswarex_location_id && r.channel_location_id);
      await axios.patch(`${API_BASE_URL}/integrations/flipkart/mapping${chq(selectedId)}`, {
        warehouse_mapping: rows,
        default_location_id: defaultLocationId || null,
      }, headers());
      toast.success('Warehouse mapping saved');
      setMapOpen(false);
      loadAccount(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingMap(false); }
  };

  const connected = !isNew && status?.connected;
  const listingsRunning = listingsJob?.status === 'running';

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6" data-testid="flipkart-integration-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/integrations')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Integrations
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-500 to-amber-400 flex items-center justify-center text-white text-2xl font-bold">
          F
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">Flipkart Marketplace</h1>
          <p className="text-sm text-wt-text-muted">Sync Flipkart orders into Opswarex and push sellable inventory to Flipkart. Multiple seller accounts supported.</p>
          {connected && (
            <div className="mt-2">
              <ChannelDisplayNameField match={(c) => c.channel_type === 'flipkart' && c.id === selectedId} />
            </div>
          )}
        </div>
        <div className="ml-auto">
          {connected ? (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="text-wt-text-muted">
              <XCircle className="h-3 w-3 mr-1" /> Not connected
            </Badge>
          )}
        </div>
      </div>

      {/* Account selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="flipkart-account-tabs">
        {accounts.map(a => (
          <button
            key={a.channel_id}
            data-testid={`flipkart-account-tab-${a.channel_id}`}
            onClick={() => selectAccount(a.channel_id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              selectedId === a.channel_id
                ? 'bg-wt-text-primary text-wt-bg border-transparent font-medium'
                : 'bg-wt-surface text-wt-text-primary border-wt-border hover:border-wt-text-muted'
            }`}
          >
            {a.name || 'Flipkart'}
            {a.connected
              ? <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-green-400" />
              : <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-zinc-500" />}
          </button>
        ))}
        <button
          data-testid="flipkart-add-account"
          onClick={() => selectAccount('new')}
          className={`px-3 py-1.5 rounded-full text-sm border flex items-center gap-1 transition-colors ${
            isNew
              ? 'bg-wt-text-primary text-wt-bg border-transparent font-medium'
              : 'bg-wt-surface text-wt-text-primary border-wt-border hover:border-wt-text-muted'
          }`}
        >
          <Plus className="h-3.5 w-3.5" /> Add Account
        </button>
      </div>

      {loading ? (
        <Card className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></Card>
      ) : (
        <>
          <Card className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-wt-text-primary">
              {isNew ? 'Connect a new Flipkart account' : 'Credentials'}
            </h2>

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1">
              <p className="font-medium flex items-center gap-1">
                <span>Use the <span className="underline">Self Access</span> tab — not Third Party Access</span>
              </p>
              <p className="text-amber-200/80">
                1. Open{' '}
                <a
                  href="https://seller.flipkart.com/index.html#dashboard/developer-access"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Flipkart Seller Hub → Manage Profile → Developer Access
                </a>{' '}(log into the seller account you want to connect).
              </p>
              <p className="text-amber-200/80">
                2. Click the <strong>Self Access</strong> tab, then <strong>Create New Access</strong> with
                application name <code>Opswarex</code> and scope <code>Seller_Api</code>. Copy the{' '}
                <strong>Application ID</strong> and <strong>Application Secret</strong>.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account Name</Label>
                <Input
                  data-testid="flipkart-account-name"
                  placeholder="e.g. WoodenTwist Main"
                  value={accountName} onChange={(e) => setAccountName(e.target.value)}
                />
                <p className="text-xs text-wt-text-muted mt-1">Shown on orders so you know which account they came from.</p>
              </div>
              <div>
                <Label>Application ID</Label>
                <Input
                  data-testid="flipkart-app-id"
                  placeholder={connected ? status.app_id_preview : 'e.g. 36a17224a4b418...'}
                  value={appId} onChange={(e) => setAppId(e.target.value)}
                />
              </div>
              <div>
                <Label>Application Secret</Label>
                <Input
                  data-testid="flipkart-app-secret"
                  type="password"
                  placeholder={connected ? '••••••••' : 'Paste your App Secret'}
                  value={appSecret} onChange={(e) => setAppSecret(e.target.value)}
                />
              </div>
              <div>
                <Label>Environment</Label>
                <select
                  data-testid="flipkart-env"
                  className="w-full mt-1 bg-wt-surface text-wt-text-primary border border-wt-border rounded-md px-3 py-2 text-sm"
                  value={environment} onChange={(e) => setEnvironment(e.target.value)}
                >
                  <option value="production">Production</option>
                  <option value="sandbox">Sandbox</option>
                </select>
              </div>
              <div>
                <Label>Default Flipkart Location ID</Label>
                <Input
                  data-testid="flipkart-default-loc"
                  placeholder="auto-filled by Sync Listings"
                  value={defaultLocationId} onChange={(e) => setDefaultLocationId(e.target.value)}
                />
                <p className="text-xs text-wt-text-muted mt-1">
                  Used when a push has no specific warehouse mapped. Sync Listings fills this automatically.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button onClick={connect} disabled={connecting} data-testid="flipkart-connect-btn">
                {connecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
                {connected ? 'Reconnect' : 'Connect'}
              </Button>
              {connected && (
                <Button variant="outline" onClick={disconnect} data-testid="flipkart-disconnect-btn">
                  Disconnect
                </Button>
              )}
            </div>
          </Card>

          {connected && (
            <Card className="p-6 space-y-4" data-testid="flipkart-orders-card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-wt-text-primary">Order Sync</h2>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Orders auto-sync every 15 minutes for all connected accounts. Approved orders
                    are ingested and reserve stock. Cancellations release the reservations. Orders
                    for unmapped SKUs go on hold.
                  </p>
                </div>
                <Button onClick={syncOrdersNow} disabled={syncingOrders} data-testid="flipkart-sync-orders">
                  {syncingOrders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Sync Now
                </Button>
              </div>
              {orderStats && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="border border-wt-border rounded-lg p-3">
                    <div className="text-xs text-wt-text-muted">Total orders</div>
                    <div className="text-xl font-bold">{orderStats.total_orders || 0}</div>
                  </div>
                  <div className="border border-wt-border rounded-lg p-3">
                    <div className="text-xs text-wt-text-muted">On hold</div>
                    <div className="text-xl font-bold text-amber-400">{orderStats.on_hold_orders || 0}</div>
                  </div>
                  <div className="border border-wt-border rounded-lg p-3">
                    <div className="text-xs text-wt-text-muted">Last poll</div>
                    <div className="text-sm text-wt-text-primary">
                      {orderStats.last_poll_at
                        ? new Date(orderStats.last_poll_at).toLocaleString()
                        : 'Never'}
                    </div>
                  </div>
                  <div className="border border-wt-border rounded-lg p-3">
                    <div className="text-xs text-wt-text-muted">Last error</div>
                    <div className="text-xs text-red-400 truncate" title={orderStats.last_error}>
                      {orderStats.last_error || '—'}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {connected && (
            <Card className="p-6 space-y-4" data-testid="flipkart-listings-card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-wt-text-primary">Listings & Inventory Sync</h2>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Discovers this account's Flipkart listings (product IDs + locations) by checking
                    your SKUs against Flipkart. Runs in the background — big catalogs can take a few
                    minutes. Also auto-fills the default location ID. SKUs not listed on Flipkart are
                    skipped during inventory pushes.
                  </p>
                </div>
                <Button onClick={syncListingsNow} disabled={startingListings || listingsRunning} data-testid="flipkart-sync-listings">
                  {(startingListings || listingsRunning) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {listingsRunning ? 'Syncing…' : 'Sync Listings'}
                </Button>
              </div>
              {listingsJob && (
                <div className="space-y-2" data-testid="flipkart-listings-result">
                  {listingsRunning && (
                    <div className="w-full bg-wt-bg border border-wt-border rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-amber-400 h-2 transition-all"
                        style={{ width: `${listingsJob.total ? Math.min(100, Math.round((listingsJob.checked / listingsJob.total) * 100)) : 5}%` }}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="text-xs text-wt-text-muted">SKUs checked</div>
                      <div className="text-xl font-bold">
                        {listingsJob.checked}{listingsJob.total ? ` / ${listingsJob.total}` : ''}
                      </div>
                    </div>
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="text-xs text-wt-text-muted">Listed on Flipkart</div>
                      <div className="text-xl font-bold text-emerald-400">{listingsJob.listed}</div>
                    </div>
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="text-xs text-wt-text-muted">Not listed</div>
                      <div className="text-xl font-bold text-wt-text-muted">{listingsJob.not_listed}</div>
                    </div>
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="text-xs text-wt-text-muted">Status</div>
                      <div className={`text-sm font-medium ${
                        listingsJob.status === 'done' ? 'text-emerald-400'
                          : listingsJob.status === 'error' ? 'text-red-400' : 'text-amber-400'
                      }`}>
                        {listingsJob.status}
                        {listingsJob.status === 'error' && listingsJob.error ? ` — ${listingsJob.error}` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {connected && (
            <Card className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                    <Boxes className="h-5 w-5" /> Warehouse Mapping
                  </h2>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Pair each Opswarex warehouse with its corresponding Flipkart location ID so
                    pushes route to the right fulfilment centre.
                  </p>
                </div>
                <Button variant="outline" onClick={openMapping} data-testid="flipkart-edit-mapping">
                  <Link2 className="h-4 w-4 mr-2" /> Edit Mapping
                </Button>
              </div>

              {status?.warehouse_mapping?.length > 0 ? (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-wt-bg text-left text-wt-text-muted">
                      <tr>
                        <th className="p-3">Opswarex warehouse</th>
                        <th className="p-3">Flipkart location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {status.warehouse_mapping.map((m, i) => (
                        <tr key={i} className="border-t border-wt-border">
                          <td className="p-3 font-mono text-xs">{m.opswarex_location_id}</td>
                          <td className="p-3 font-mono text-xs text-blue-400">{m.channel_location_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-wt-text-muted italic">
                  No warehouses mapped yet. All pushes will go to the default location.
                </p>
              )}
            </Card>
          )}

          {connected && (
            <Card className="p-6 space-y-4" data-testid="flipkart-settings-card">
              <div>
                <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Fulfillment Preferences
                </h2>
                <p className="text-xs text-wt-text-muted mt-1">
                  Controls how labels &amp; invoices print for this account's Flipkart orders.
                </p>
              </div>

              <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-wt-border bg-wt-bg">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-wt-text-primary">
                    Split Shipping Labels &amp; Invoice
                  </Label>
                  <p className="text-xs text-wt-text-muted mt-1">
                    When enabled, "Label" in the order detail downloads the shipping label and tax
                    invoice as <span className="text-wt-text-primary font-medium">two separate PDFs</span>.
                    When disabled, Flipkart's combined label+invoice PDF is used (default).
                  </p>
                </div>
                <Switch
                  data-testid="flipkart-split-labels-toggle"
                  checked={splitLabelsInvoice}
                  disabled={savingSettings}
                  onCheckedChange={toggleSplitLabelsInvoice}
                />
              </div>
            </Card>
          )}

          {connected && (
            <Card className="p-6 space-y-4" data-testid="flipkart-returns-card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-wt-text-primary">Returns &amp; RTO</h2>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Customer returns and RTO shipments are pulled every 30 minutes across all
                    accounts. RTO / seller-received items restock the mapped Opswarex SKU and
                    fan-out to all other channels automatically.
                  </p>
                </div>
                <Button
                  onClick={syncReturnsNow}
                  disabled={syncingReturns}
                  data-testid="flipkart-sync-returns"
                >
                  {syncingReturns ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Sync Returns Now
                </Button>
              </div>

              {returns.length === 0 ? (
                <p className="text-sm text-wt-text-muted italic">No returns yet.</p>
              ) : (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-wt-bg text-left text-wt-text-muted">
                      <tr>
                        <th className="p-3">Return ID</th>
                        <th className="p-3">Order Item</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Qty</th>
                        <th className="p-3">Created</th>
                      </tr>
                    </thead>
                    <tbody data-testid="flipkart-returns-table">
                      {returns.slice(0, 20).map((ret) => (
                        <tr key={ret.id} className="border-t border-wt-border">
                          <td className="p-3 font-mono text-xs">{ret.return_id}</td>
                          <td className="p-3 font-mono text-xs text-blue-400">{ret.order_item_id}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {(ret.type || '').replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="p-3">{ret.quantity}</td>
                          <td className="p-3 text-xs text-wt-text-muted">
                            {ret.created_at ? new Date(ret.created_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Mapping Dialog */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Opswarex → Flipkart Warehouse Mapping</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-wt-text-muted">
              For each Opswarex warehouse, enter the corresponding Flipkart location ID (as shown
              on your Flipkart Seller Dashboard → Manage Locations).
            </p>
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-2">
              {mappingRows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <select
                    data-testid={`flipkart-map-opswarex-${idx}`}
                    className="bg-wt-surface text-wt-text-primary border border-wt-border rounded-md px-3 py-2 text-sm"
                    value={row.opswarex_location_id}
                    onChange={(e) => updateRow(idx, 'opswarex_location_id', e.target.value)}
                  >
                    <option value="">Select warehouse…</option>
                    {opswarexWarehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name || w.id}</option>
                    ))}
                  </select>
                  <Input
                    data-testid={`flipkart-map-channel-${idx}`}
                    placeholder="Flipkart location_id"
                    value={row.channel_location_id}
                    onChange={(e) => updateRow(idx, 'channel_location_id', e.target.value)}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeRow(idx)} data-testid={`flipkart-map-remove-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addRow} data-testid="flipkart-map-add">
              + Add mapping
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMapOpen(false)}>Cancel</Button>
            <Button onClick={saveMapping} disabled={savingMap} data-testid="flipkart-map-save">
              {savingMap ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlipkartIntegrationPage;
